import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface VarianceSummary {
  contract_value: number;
  estimated_cost: number;
  committed_cost: number;
  actual_cost: number;
  estimated_margin_pct: number;
  committed_margin_pct: number;
  actual_margin_pct: number;
  committed_margin_variance: number;
  actual_margin_variance: number;
}

export interface VarianceDrivers {
  override_exposure: number;
  override_line_count: number;
  po_overrun: number;
  po_overrun_line_count: number;
  attributed_actual: number;
  unattributed_actual: number;
  unattributed_expense_count: number;
  unattributed_po_count: number;
}

export interface OverrideLine {
  sov_line_description: string;
  estimated_rate: number;
  override_rate: number;
  quantity: number;
  impact: number;
  reason: string | null;
  approved_at: string | null;
}

export interface UnattributedExpense {
  description: string;
  amount: number;
  category: string | null;
}

export interface DataQuality {
  wo_lines_missing_sov_rate: number;
  unattributed_expense_count: number;
  unattributed_po_count: number;
}

export interface VarianceDriversData {
  summary: VarianceSummary;
  drivers: VarianceDrivers;
  top_overrides: OverrideLine[];
  top_unattributed: UnattributedExpense[];
  data_quality: DataQuality;
}

export const useProjectVarianceDrivers = (projectId: string | undefined) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["project_variance_drivers", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_project_variance_drivers" as any, {
        p_project_id: projectId!,
      });
      if (error) throw error;
      const raw = data as unknown as VarianceDriversData;
      return {
        summary: {
          contract_value: Number(raw.summary.contract_value ?? 0),
          estimated_cost: Number(raw.summary.estimated_cost ?? 0),
          committed_cost: Number(raw.summary.committed_cost ?? 0),
          actual_cost: Number(raw.summary.actual_cost ?? 0),
          estimated_margin_pct: Number(raw.summary.estimated_margin_pct ?? 0),
          committed_margin_pct: Number(raw.summary.committed_margin_pct ?? 0),
          actual_margin_pct: Number(raw.summary.actual_margin_pct ?? 0),
          committed_margin_variance: Number(raw.summary.committed_margin_variance ?? 0),
          actual_margin_variance: Number(raw.summary.actual_margin_variance ?? 0),
        },
        drivers: {
          override_exposure: Number(raw.drivers.override_exposure ?? 0),
          override_line_count: Number(raw.drivers.override_line_count ?? 0),
          po_overrun: Number(raw.drivers.po_overrun ?? 0),
          po_overrun_line_count: Number(raw.drivers.po_overrun_line_count ?? 0),
          attributed_actual: Number(raw.drivers.attributed_actual ?? 0),
          unattributed_actual: Number(raw.drivers.unattributed_actual ?? 0),
          unattributed_expense_count: Number(raw.drivers.unattributed_expense_count ?? 0),
          unattributed_po_count: Number(raw.drivers.unattributed_po_count ?? 0),
        },
        top_overrides: (raw.top_overrides ?? []).map((o: any) => ({
          sov_line_description: o.sov_line_description ?? "",
          estimated_rate: Number(o.estimated_rate ?? 0),
          override_rate: Number(o.override_rate ?? 0),
          quantity: Number(o.quantity ?? 0),
          impact: Number(o.impact ?? 0),
          reason: o.reason ?? null,
          approved_at: o.approved_at ?? null,
        })),
        top_unattributed: (raw.top_unattributed ?? []).map((e: any) => ({
          description: e.description ?? "",
          amount: Number(e.amount ?? 0),
          category: e.category ?? null,
        })),
        data_quality: {
          wo_lines_missing_sov_rate: Number(raw.data_quality.wo_lines_missing_sov_rate ?? 0),
          unattributed_expense_count: Number(raw.data_quality.unattributed_expense_count ?? 0),
          unattributed_po_count: Number(raw.data_quality.unattributed_po_count ?? 0),
        },
      } as VarianceDriversData;
    },
    enabled: !!user && !!projectId,
  });
};
